"use client";

/**
 * MarkdownViewerPage — standalone full-viewport markdown viewer for the
 * pop-out tab (`/session/<id>?panel=file:...&popout=1`).
 *
 * Renders inside the AuthKit boundary, so it shares theme, i18n, and auth
 * context with the rest of the app. The file URL is still self-contained,
 * so `useMarkdownFile` fetches it without an extra auth header.
 *
 * Sets the browser tab title to the filename and restores the default app
 * title on unmount. Nested `.md` links open additional pop-out tabs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";
import type { Editor } from "@tiptap/core";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { MarkdownViewer } from "@/components/markdown-viewer";
import { MarkdownOutline, MarkdownOutlineTop } from "@/components/markdown-outline";
import { MarkdownViewerToolbar } from "@/components/markdown-viewer-toolbar";
import { MarkdownViewerPinButton } from "@/components/markdown-viewer-pin-button";
import { MarkdownViewerSaveStatus } from "@/components/markdown-viewer-save-status";
import { CollaborativeMarkdownPanel, useCollabCapability } from "@/components/collaborative-markdown-panel";
import { useMarkdownFile } from "@/hooks/use-markdown-file";
import { useMarkdownCrossFileProjection } from "@/lib/markdown-cross-file-links";
import { extractFilePathFromToken } from "@/lib/file-link-rewrite";
import { extractFileToken } from "@/lib/file-links";
import type { AccessTokenGetter } from "@/lib/utils";

export interface MarkdownViewerPageProps {
  sessionId?: string | null;
  /** Signed file-download URL (``/files/d/{jwt}``). */
  url: string;
  /** Filename for display (from the JWT fn claim or link label). */
  fileName: string;
  /** Token getter from the authenticated ChatPage that mounted this pop-out. */
  getToken: AccessTokenGetter;
  /** Called when the user wants to close the pop-out page. */
  onClose: () => void;
  /** Manager-owned URL builder for nested `.md` pop-out links. */
  buildPopoutUrl: (url: string, fileName: string) => string;
  onPinFile?: (path: string) => Promise<void> | void;
  isFilePinned?: (path: string) => boolean;
  pinningFilePath?: string | null;
}

export function MarkdownViewerPage(props: MarkdownViewerPageProps) {
  const { t } = useTranslation();
  const capability = useCollabCapability(props.url, props.getToken, props.fileName);
  useEffect(() => {
    document.title = props.fileName;
    return () => { document.title = import.meta.env.APP_NAME ?? "Layer Nine"; };
  }, [props.fileName]);

  if (capability.kind === "available") {
    return <CollaborativeMarkdownPanel {...props} room={capability.room} fullscreen />;
  }
  if (capability.kind === "loading") {
    return <div className="flex h-screen items-center justify-center bg-background" role="status" aria-label={t("common:status.loadingDocument")}>
      <Spinner className="size-5" />
    </div>;
  }
  return <LegacyMarkdownViewerPage {...props} readOnly={capability.kind === "readOnly"} />;
}

function LegacyMarkdownViewerPage({
  sessionId,
  url,
  fileName,
  getToken,
  onClose,
  buildPopoutUrl,
  onPinFile,
  isFilePinned,
  pinningFilePath,
  readOnly,
}: MarkdownViewerPageProps & { readOnly: boolean }) {
  const { t } = useTranslation();
  const [editor, setEditor] = useState<Editor | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const [editable, setEditable] = useState(false);
  const [savedMarkdown, setSavedMarkdown] = useState<string | null>(null);
  const [currentMarkdown, setCurrentMarkdown] = useState<string | null>(null);
  const currentMarkdownRef = useRef<string | null>(null);
  const activeFilePathRef = useRef<string | null>(null);
  const activeDocumentIdentityRef = useRef<string | null>(null);
  const filePath = useMemo(() => {
    const token = extractFileToken(url);
    return token ? extractFilePathFromToken(token) : null;
  }, [url]);
  const pinned = filePath ? Boolean(isFilePinned?.(filePath)) : false;
  const pinning = filePath != null && pinningFilePath === filePath;
  const dirty =
    savedMarkdown !== null &&
    currentMarkdown !== null &&
    currentMarkdown !== savedMarkdown;
  const {
    content,
    loading,
    error,
    refetch,
    refreshed,
    save,
    saving,
    saveError,
    conflict,
    remoteChange,
    saveOutcomeUnknown,
    checkLatest,
    reloadLatest,
  } = useMarkdownFile(url, {
    // Some policy-authorized links intentionally omit the readable path
    // claim. The pop-out's filename keeps a reissued capability in place.
    documentIdentity: fileName,
    pollingPaused: false,
    detectOnly: editable || dirty,
  });
  const renderContent = useMarkdownCrossFileProjection(content, url, sessionId, getToken);

  // A pop-out can stay mounted while its query-string URL changes. Signed
  // links are reissued over time, so only reset local state when the decoded
  // file path (or opaque capability's filename) actually changes; a new
  // capability for the same file is a background refresh and must leave the
  // mounted document alone.
  useEffect(() => {
    const previousFilePath = activeFilePathRef.current;
    const sameFileRefresh = previousFilePath !== null || filePath !== null
      ? previousFilePath !== null && previousFilePath === filePath
      : activeDocumentIdentityRef.current !== null
        && activeDocumentIdentityRef.current === fileName;
    activeFilePathRef.current = filePath;
    activeDocumentIdentityRef.current = fileName;
    if (sameFileRefresh) return;
    setEditable(false);
    setSavedMarkdown(null);
    setCurrentMarkdown(null);
    currentMarkdownRef.current = null;
  }, [fileName, filePath, url]);

  const handleMarkdownLoaded = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setSavedMarkdown(markdown);
    setCurrentMarkdown(markdown);
  }, []);

  const handleMarkdownChange = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setCurrentMarkdown(markdown);
  }, []);

  const handleSave = useCallback(async (markdown?: string): Promise<boolean> => {
    const markdownToSave = markdown ?? currentMarkdown;
    if (markdownToSave === null) return false;
    if (markdownToSave === savedMarkdown) return true;

    const didSave = await save(markdownToSave);
    if (didSave) {
      setSavedMarkdown(markdownToSave);
      const latestMarkdown = currentMarkdownRef.current ?? markdownToSave;
      currentMarkdownRef.current = latestMarkdown;
      setCurrentMarkdown(latestMarkdown);
    }
    return didSave;
  }, [currentMarkdown, savedMarkdown, save]);

  const handleToggleEdit = useCallback(() => {
    if (readOnly || conflict || saveOutcomeUnknown) return;
    setEditable((isEditable) => !isEditable);
  }, [readOnly, conflict, saveOutcomeUnknown]);

  const reloadAndDiscard = useCallback(async () => {
    if (!window.confirm(t("viewer.reloadDiscardPrompt"))) return;
    const wasEditable = editable;
    setEditable(false);
    const latest = await reloadLatest();
    if (latest === null) {
      setEditable(wasEditable);
      return;
    }
    currentMarkdownRef.current = latest;
    setSavedMarkdown(latest);
    setCurrentMarkdown(latest);
  }, [editable, reloadLatest, t]);

  const checkSaveOutcome = useCallback(async () => {
    const result = await checkLatest();
    if (result.kind !== "reconciled") return;
    setSavedMarkdown(result.content);
    if (currentMarkdownRef.current === null) {
      currentMarkdownRef.current = result.content;
      setCurrentMarkdown(result.content);
    }
  }, [checkLatest]);

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b px-4 py-3 gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <MarkdownViewerSaveStatus
            contentLoaded={currentMarkdown != null}
            editable={editable}
            hasSaveHandler={!readOnly && !conflict && !saveOutcomeUnknown}
            dirty={dirty}
            saving={saving}
            saveError={saveError}
          />
          <span className="truncate text-sm font-semibold" title={fileName}>
            {fileName}
          </span>
          {refreshed && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-blue-500"
              title={t("viewer.contentUpdated")}
              data-testid="markdown-viewer-refreshed-indicator"
            />
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <MarkdownViewerPinButton
            filePath={filePath}
            onPinFile={onPinFile}
            pinned={pinned}
            pinning={pinning}
          />
          {!readOnly && <MarkdownViewerToolbar
            editor={editor}
            sourceMarkdown={dirty ? currentMarkdown : content}
            fileName={fileName}
            getToken={getToken}
            fileUrl={url}
            editable={editable}
            dirty={dirty}
            saving={saving}
            saveError={saveError}
            saveBlocked={Boolean(conflict || saveOutcomeUnknown)}
            onToggleEdit={handleToggleEdit}
            onSave={conflict || saveOutcomeUnknown ? undefined : handleSave}
            contentVersion={currentMarkdown}
          />}
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            aria-label={t("viewer.closeViewer")}
          >
            <span aria-hidden className="text-sm font-medium leading-none">
              ×
            </span>
          </Button>
        </div>
      </div>

      {/* The outline overlays this content region, matching the desktop sidebar. */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div ref={contentScrollRef} className="h-full min-w-0 overflow-y-auto overflow-x-hidden p-6">
          {content != null ? (
            <>
              {readOnly && <p role="status" className="mb-2 rounded bg-muted p-2 text-xs">{t("viewer.collabReadOnly")}</p>}
              {remoteChange && !conflict && (
                <p className="mb-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  {t("viewer.remoteChange")}
                </p>
              )}
              {saveOutcomeUnknown && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("viewer.saveOutcomeUnknown")}</span>
                  <Button size="sm" variant="outline" onClick={() => void checkSaveOutcome()}>{t("viewer.checkLatest")}</Button>
                </div>
              )}
              {conflict && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-destructive/50 bg-destructive/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("viewer.fileChangedElsewhere")}</span>
                  <Button size="sm" variant="outline" onClick={() => void reloadAndDiscard()}>{t("viewer.reloadAndDiscard")}</Button>
                </div>
              )}
              <MarkdownOutlineTop
                editor={editor}
                scrollContainerRef={contentScrollRef}
                contentVersion={currentMarkdown ?? content}
              />
              <MarkdownViewer
                content={content}
                renderContent={!editable && !dirty ? renderContent : null}
                fileName={fileName}
                editable={!readOnly && editable}
                saving={saving}
                onEditorReady={setEditor}
                onMarkdownLoaded={handleMarkdownLoaded}
                onMarkdownChange={handleMarkdownChange}
                preserveScrollOnContentChange={refreshed && !editable}
                scrollContainerRef={contentScrollRef}
                onNavigateFile={(newUrl, newName) => {
                  if (editable || dirty || saving || conflict || saveOutcomeUnknown) return;
                  // Nested .md links open another viewer tab.
                  const popoutUrl = buildPopoutUrl(newUrl, newName);
                  window.open(popoutUrl, "_blank", "noopener");
                }}
              />
            </>
          ) : loading ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <Spinner className="size-5" />
              <p className="text-sm">{t("common:status.loadingDocument")}</p>
            </div>
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center p-4">
              <p className="text-sm text-destructive">
                {sessionId ? t("chat:crossFileLinks.unavailable") : error}
              </p>
              {(error?.includes("401") || error?.includes("403")) && (
                <p className="text-sm text-muted-foreground">
                  {t("viewer.linkExpired")}
                </p>
              )}
              <Button variant="outline" size="sm" onClick={refetch}>
                {t("common:actions.retry")}
              </Button>
            </div>
          ) : null}
        </div>
        <MarkdownOutline
          editor={editor}
          scrollContainerRef={contentScrollRef}
          contentVersion={currentMarkdown ?? content}
          defaultExpanded
        />
      </div>
    </div>
  );
}
